import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
} from '@mui/material';
import {  
  InfoOutlined,
  TrendingUp,
  Assessment,
  ExpandMore,
  Refresh as RefreshIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import OCIBadge from '../../components/oci/OCIBadge';
import OCIFactorsList from '../../components/oci/OCIFactorsList';
import { ociService } from '../../services/ociService';
import { usePagesList } from '../../hooks/usePagesList';

export default function OciAnalyticsPage() {
  const [selectedPageId, setSelectedPageId] = useState<number>(2);
  const [engine, setEngine] = useState<'google' | 'bing'>('google');
  const [window, setWindow] = useState<7 | 28 | 90>(28);

  // Use the common prepopulated pages list
  const { pages, loading: pagesLoading, error: pagesError } = usePagesList();

  // Fetch OCI score
  const ociQuery = useQuery({
    queryKey: ['oci', selectedPageId, engine, window],
    queryFn: () => ociService.getOCIScore(selectedPageId, engine, window),
    enabled: !!selectedPageId,
  });

  // Fetch OCI history
  const historyQuery = useQuery({
    queryKey: ['ociHistory', selectedPageId, engine, window],
    queryFn: () => ociService.getOCIHistory(selectedPageId, engine, window),
    enabled: !!selectedPageId,
  });

  // Recompute mutation
  const recomputeMutation = useMutation({
    mutationFn: () => ociService.recomputeOCI(selectedPageId, engine, window),
    onSuccess: () => {
      ociQuery.refetch();
      historyQuery.refetch();
    },
  });

  const ociData = ociQuery.data;
  console.log('🎯 [OCI PAGE] ociQuery:', { isLoading: ociQuery.isLoading, isError: ociQuery.isError, data: ociData });
  console.log('🎯 [OCI PAGE] ociData.factors:', ociData?.factors, 'Type:', typeof ociData?.factors, 'IsArray:', Array.isArray(ociData?.factors));
  
  const insufficientData = ociData?.minImpressionsRequired && 
    ociData?.actualImpressions && 
    ociData.actualImpressions < ociData.minImpressionsRequired;

  return (
    <Box sx={{ p: 2 }}>
      {/* Info Panels - Collapsible */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <InfoOutlined color="info" sx={{ mr: 1.5 }} />
          <Typography variant="subtitle1" fontWeight="bold">How OCI Works</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" component="div" sx={{ fontSize: '0.875rem', mb: 2 }}>
            OCI combines <strong>4 first-party signals</strong> from your Google Search Console / Bing Webmaster data:
          </Typography>
          <Stack spacing={1}>
            <Box>
              <Chip label="1" size="small" color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span" fontWeight="bold">Impression Volatility: </Typography>
              <Typography variant="body2" component="span" color="text.secondary">
                High daily fluctuation = competitive SERP with frequent ranking changes
              </Typography>
            </Box>
            <Box>
              <Chip label="2" size="small" color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span" fontWeight="bold">Position Variance: </Typography>
              <Typography variant="body2" component="span" color="text.secondary">
                Unstable rankings indicate active competition for keywords
              </Typography>
            </Box>
            <Box>
              <Chip label="3" size="small" color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span" fontWeight="bold">CTR Responsiveness: </Typography>
              <Typography variant="body2" component="span" color="text.secondary">
                How much CTR changes with position shifts reveals click competition intensity
              </Typography>
            </Box>
            <Box>
              <Chip label="4" size="small" color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span" fontWeight="bold">Experiment Win Rate: </Typography>
              <Typography variant="body2" component="span" color="text.secondary">
                Historical A/B test success shows how hard it is to improve this page
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            <strong>Final Score:</strong> 0-100 (LOW/MEDIUM/HIGH/VERY_HIGH) showing competitiveness level
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <TrendingUp color="success" sx={{ mr: 1.5 }} />
          <Typography variant="subtitle1" fontWeight="bold">Business Value</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Use OCI to make data-driven investment decisions:
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="body2"><strong>LOW OCI (0-33):</strong></Typography>
              <Typography variant="body2" color="text.secondary">
                ✅ Good opportunity - Low competition detected. Small improvements likely to yield results. <strong>Recommended for quick wins.</strong>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2"><strong>MEDIUM OCI (34-66):</strong></Typography>
              <Typography variant="body2" color="text.secondary">
                ⚠️ Moderate competition - Focused effort recommended. Need quality content + technical optimization. <strong>ROI depends on execution quality.</strong>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2"><strong>HIGH OCI (67-85):</strong></Typography>
              <Typography variant="body2" color="text.secondary">
                ⚠️ High competition - Substantial resources required. Expect longer timelines, need strong E-E-A-T, quality backlinks, and ongoing optimization. <strong>High effort, delayed ROI.</strong>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2"><strong>VERY HIGH OCI (86-100):</strong></Typography>
              <Typography variant="body2" color="text.secondary">
                ❌ Extremely competitive - Consider alternative strategies. May be better to target long-tail variations or create differentiated content angles. <strong>Very low success probability.</strong>
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Assessment color="action" sx={{ mr: 1.5 }} />
          <Typography variant="subtitle1" fontWeight="bold">Technical Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" component="div" sx={{ fontSize: '0.875rem' }}>
            • <strong>Data Source:</strong> 100% first-party (Google Search Console + Bing Webmaster Tools)<br />
            • <strong>Analysis Window:</strong> 28 or 90 days (configurable)<br />
            • <strong>Cache:</strong> Scores refresh every 24 hours (click "Recompute" to force update)<br />
            • <strong>Engine Support:</strong> Separate OCI for Google vs Bing (different competitive landscapes)<br />
            • <strong>Privacy:</strong> No third-party keyword databases or competitor scraping
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* OCI Analysis Dashboard */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Observed Competitiveness Index (OCI)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Page #{selectedPageId} - Investment Priority Analysis
        </Typography>

        {/* Controls */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Stack spacing={2}>
            {/* Page Selector */}
            <Autocomplete
              options={pages}
              getOptionLabel={(option) => option.label}
              value={pages.find((p) => p.id === selectedPageId) || null}
              onChange={(_, newValue) => newValue && setSelectedPageId(newValue.id)}
              loading={pagesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Page"
                  size="small"
                  helperText={pagesError ? 'Failed to load pages' : ''}
                  error={!!pagesError}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <ArticleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2">{option.url}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option.id}
                    </Typography>
                  </Box>
                </Box>
              )}
              fullWidth
              size="small"
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Engine Selector */}
              <ToggleButtonGroup
                value={engine}
                exclusive
                onChange={(_, val) => val && setEngine(val)}
                size="small"
              >
                <ToggleButton value="google">GOOGLE</ToggleButton>
                <ToggleButton value="bing">BING</ToggleButton>
              </ToggleButtonGroup>

              {/* Window Selector */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Time Window</InputLabel>
                <Select
                  value={window}
                  label="Time Window"
                  onChange={(e) => setWindow(e.target.value as 7 | 28 | 90)}
                >
                  <MenuItem value={7}>7 days</MenuItem>
                  <MenuItem value={28}>28 days</MenuItem>
                  <MenuItem value={90}>90 days</MenuItem>
                </Select>
              </FormControl>

              {/* Recompute Button */}
              <Button
                variant="outlined"
                startIcon={recomputeMutation.isPending ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={() => recomputeMutation.mutate()}
                disabled={recomputeMutation.isPending || !selectedPageId}
                size="small"
              >
                Recompute
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* OCI Results */}
        {ociQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : ociQuery.isError ? (
          ociQuery.error?.message === 'OCI_COMPUTING' ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>OCI Computation In Progress</strong>
              <br />
              The {window}-day OCI score is being calculated for this page. Please check back in a few minutes.
            </Alert>
          ) : (
            <Alert severity="error">Failed to load OCI data: {ociQuery.error?.message || 'Unknown error'}</Alert>
          )
        ) : insufficientData ? (
          <Alert severity="warning">
            Insufficient data to calculate OCI. Need at least {ociData?.minImpressionsRequired} impressions (found {ociData?.actualImpressions})
          </Alert>
        ) : ociData ? (
          <Box>
            {/* OCI Score Badge */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <OCIBadge score={ociData.score} level={ociData.level} size="large" />
            </Box>

            {/* OCI Factors */}
            <OCIFactorsList factors={ociData.factors} />

            {/* Metadata */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Calculated:</strong> {new Date(ociData.computedAt).toLocaleString()}<br />
                <strong>Data Period:</strong> {window} days<br />
                <strong>Engine:</strong> {engine.toUpperCase()}<br />
                <strong>Impressions:</strong> {ociData.actualImpressions?.toLocaleString() || 'N/A'}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}
